<script setup lang="ts">
    import { useListen, useEvent } from '@/composables/useEventBus';
    const { $theme } = useNuxtApp();
    const $emit = defineEmits<{
        (e: 'before-open', payload: any): void;
        (e: 'opened', payload: any): void;
        (e: 'before-close', payload: any): void;
        (e: 'closed', payload: any): void;
    }>();
    const { getModalZIndex, setPersistent } = useModalsStore();

    const props = defineProps({
        name: {
            required: true,
            type: String,
        },
        modalStyle: {
            required: false,
            type: String,
            default: 'centered',
        },
        width: {
            type: Number,
            required: false,
            default: 600,
        },
        showCloseIcon: {
            required: false,
            type: Boolean,
            default: true,
        },
        contentClasses: {
            type: String,
            required: false,
            default: '',
        },
        transition: {
            required: false,
            type: String,
            default: 'vm-transition--modal',
        },
        slotContainerClasses: {
            required: false,
            type: String,
            default: 'w-full h-full',
        },
        scrollable: {
            required: false,
            type: Boolean,
            default: false,
        },
        persistent: {
            required: false,
            type: Boolean,
            default: false,
        },
        // Opt-in dialog semantics (TDD-5194). Absent by default so the 40+ existing call sites
        // render exactly the markup they render today; a modal that is a real dialog passes
        // `role="dialog"`, `aria-modal` and the id of its own title.
        role: {
            required: false,
            type: String,
            default: undefined,
        },
        ariaModal: {
            required: false,
            type: Boolean,
            default: undefined,
        },
        ariaLabelledby: {
            required: false,
            type: String,
            default: undefined,
        },
    });

    // `aria-modal="false"` is not the same as no attribute at all, so only emit it when opted in.
    const ariaModalAttribute = computed<'true' | undefined>(() => (props.ariaModal ? 'true' : undefined));

    const modelContentClasses = computed(() => props.contentClasses || $theme(`modal.${props.modalStyle}.content`));

    // When `scrollable`, the inner slot wrapper owns the scroll on desktop (it has a
    // capped max-height there). The outer panel also declaring overflow-y-auto then
    // produces a double scrollbar. Neutralise the outer scroll on desktop only —
    // on mobile the inner wrapper has no height cap, so the outer must keep scrolling.
    const innerContainerClasses = computed(() => {
        const themeClasses = $theme(`modal.${props.modalStyle}.container.inner`);
        if (!props.scrollable) return themeClasses;
        return `${themeClasses} lg:overflow-y-hidden`;
    });

    // Centered styles get an inline max-width (the centered_mobile_full variant
    // keeps min-w-[100%] on mobile so it still goes fullscreen there).
    const isCenteredStyle = computed(() => ['centered', 'centered_mobile_full', 'centered_profile_review', 'centered_profile_review_dialog'].includes(props.modalStyle));
    const centeredMaxHeightClass = computed(() => {
        if (props.modalStyle === 'centered' || props.modalStyle === 'centered_profile_review_dialog') return 'centered-modal-max-height';
        // centered_mobile_full: fullscreen on mobile, capped height only on desktop.
        if (props.modalStyle === 'centered_mobile_full' || props.modalStyle === 'centered_profile_review') return 'centered-modal-max-height-lg';
        return '';
    });

    // `centered` caps the slot at the viewport height at every
    // width, but only a `scrollable` modal lets it scroll. Anything taller than the screen
    // was therefore cut off with no way down to it. On a phone, where the browser bars eat
    // into the height, the sent-document warning lost its checkbox and both its buttons.
    //
    // Below `lg` only, the boundary the rest of the app switches to its mobile layout on:
    // a desktop modal keeps exactly the box it had. The close button is outside this box
    // (absolutely placed on the content), so it stays in view while the body scrolls.
    const mobileOverflowClass = computed(() => {
        if (props.scrollable) return '';
        return centeredMaxHeightClass.value === 'centered-modal-max-height' ? 'max-lg:overflow-y-auto' : '';
    });

    const modalContainer = ref<HTMLElement | null>(null);
    const opened = ref(false);

    const open = () => {
        $emit('before-open', props.name);
        if (props.persistent) {
            setPersistent(props.name, true);
        }
        opened.value = true;
        $emit('opened', props.name);
        nextTick(() => {
            (modalContainer.value as HTMLElement)?.focus();
        });
    };

    const close = () => {
        $emit('before-close', props.name);
        setPersistent(props.name, false);
        opened.value = false;
        $emit('closed', props.name);
    };

    useListen('modal:open', (name: string) => {
        if (name === props.name) {
            open();
        }
    });

    useListen('modal:close', (name: string) => {
        if (name === props.name && opened.value) {
            close();
        }
    });

    defineExpose({
        open,
        close,
        opened,
    });
</script>

<template>
    <transition
        :enter-active-class="$theme(`modal.${modalStyle}.transition.enter-active-class`)"
        :enter-from-class="$theme(`modal.${modalStyle}.transition.enter-from-class`)"
        :enter-to-class="$theme(`modal.${modalStyle}.transition.enter-to-class`)"
        :leave-active-class="$theme(`modal.${modalStyle}.transition.leave-active-class`)"
        :leave-from-class="$theme(`modal.${modalStyle}.transition.leave-from-class`)"
        :leave-to-class="$theme(`modal.${modalStyle}.transition.leave-to-class`)"
    >
        <div
            v-if="opened"
            class="pointer-events-none"
            :class="$theme(`modal.${modalStyle}.container.outer`)"
            :data-modal="name"
            :style="`z-index: ${getModalZIndex(name)};`"
            @click.stop
        >
            <!-- Navigation arrows slot (left side) -->
            <div
                v-if="$slots.navigation"
                class="absolute sm:right-[540px] md:right-[600px] xl:right-[700px] 2xl:right-[800px] top-4 -translate-x-1/2 mr-2 z-10 flex flex-col gap-2 pointer-events-auto"
            >
                <slot name="navigation" />
            </div>
            <div
                v-if="$slots.navigation"
                class="absolute sm:right-[540px] md:right-[600px] xl:right-[700px] 2xl:right-[800px] top-4 -translate-x-1/2 mr-2 z-10 flex flex-col gap-2 pointer-events-auto"
            >
                <slot name="navigation" />
            </div>
            <div
                ref="modalContainer"
                :class="innerContainerClasses"
                class="pointer-events-auto"
                :style="isCenteredStyle ? `max-width: ${props.width}px; width: 100%` : ''"
                :tabindex="opened ? 100 : -1"
                :role="role"
                :aria-modal="ariaModalAttribute"
                :aria-labelledby="ariaLabelledby"
            >
                <div :class="modelContentClasses">
                    <div
                        v-if="showCloseIcon && !persistent && !$slots.navigation"
                        :class="$theme('modal.closeIcon.container')"
                    >
                        <button
                            :class="$theme('modal.closeIcon.button')"
                            @click="useEvent('modal:close', name)"
                        >
                            <i class="fa-regular fa-xmark"></i>
                        </button>
                    </div>
                    <div :class="[slotContainerClasses, scrollable ? 'overflow-y-auto nice-scrollbar' : '', centeredMaxHeightClass, mobileOverflowClass]">
                        <slot></slot>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<style>
    .centered-modal-max-height {
        max-height: calc(100dvh - 40px);
    }
    @media (min-width: 1024px) {
        .centered-modal-max-height-lg {
            max-height: calc(100dvh - 40px);
        }
    }
</style>
